import { httpClient } from '../client';
import {
  Form,
  CreateFormDTO,
  UpdateFormDTO,
  SingleFormResponse,
  FormSubmission,
  FormValuesListResponse,
} from '../../../types/form';
import { logger } from '../../../utils/logger';
import { ApiErrorHandler } from '../error.handler';
import { BaseEndpoint } from './base.endpoint';

// API response type with formValue as JSON string
interface DynamicFormApiItem {
  formId: string;
  formValue: string; // JSON stringified Form data
  formName: string;
  uuid: string;
  serviceType: string;
}

interface DynamicFormApiResponse {
  data: DynamicFormApiItem[];
  count: number;
}

interface SingleDynamicFormApiResponse {
  data: DynamicFormApiItem;
}

class FormEndpoints extends BaseEndpoint {
  private static instance: FormEndpoints;

  private constructor() {
    super('organisations');
  }

  static getInstance(): FormEndpoints {
    if (!FormEndpoints.instance) {
      FormEndpoints.instance = new FormEndpoints();
    }
    return FormEndpoints.instance;
  }

  // Parse the formValue JSON string to get the actual Form data
  private parseFormValue(item: DynamicFormApiItem): Form {
    try {
      const parsed = JSON.parse(item.formValue) as Form;
      // Use formName from outer object if not in parsed data
      // Include uuid from outer object for API operations
      return {
        ...parsed,
        uuid: item.uuid, // API uses uuid for GET/PUT/DELETE
        formId: parsed.formId || item.formId,
        formName: parsed.formName || item.formName,
      };
    } catch (error) {
      logger.error('Failed to parse formValue:', error);
      // Return a minimal form object if parsing fails
      return {
        uuid: item.uuid, // API uses uuid for GET/PUT/DELETE
        formId: item.formId,
        clientId: '',
        formName: item.formName,
        triggerPrompt: '',
        description: '',
        requiresVerification: false,
        fields: [],
        saveLocal: true,
        successMessage: '',
        errorMessage: '',
        isActive: true,
      };
    }
  }

  async listForms(organizationId: string): Promise<Partial<Form>[]> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<
        DynamicFormApiResponse | DynamicFormApiItem[] | { data: Partial<Form>[] }
      >(`${this.basePath}/${organizationId}/dynamic-forms`, { headers });

      const responseData = response.data;

      // Handle array response directly
      if (Array.isArray(responseData)) {
        // Check if items have formValue (need parsing) or are already Form objects
        if (responseData.length > 0 && 'formValue' in responseData[0]) {
          return (responseData as DynamicFormApiItem[]).map(item => this.parseFormValue(item));
        }
        return responseData as Partial<Form>[];
      }

      // Handle {data: [...], count: ...} response
      if (responseData && 'data' in responseData && Array.isArray(responseData.data)) {
        const items = responseData.data;
        // Check if items have formValue (need parsing) or are already Form objects
        if (items.length > 0 && 'formValue' in items[0]) {
          return (items as DynamicFormApiItem[]).map(item => this.parseFormValue(item));
        }
        return items as Partial<Form>[];
      }

      return [];
    } catch (error) {
      logger.error('Failed to fetch forms:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  // dynamicFormId should be the uuid from the API response
  async getForm(organizationId: string, dynamicFormUuid: string): Promise<Form> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<
        SingleDynamicFormApiResponse | SingleFormResponse | DynamicFormApiItem | Form
      >(`${this.basePath}/${organizationId}/dynamic-forms/${dynamicFormUuid}`, { headers });

      const responseData = response.data;

      // Handle {data: ...} wrapper
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const item = (responseData as { data: DynamicFormApiItem | Form }).data;
        // Check if it has formValue (need parsing)
        if ('formValue' in item) {
          return this.parseFormValue(item as DynamicFormApiItem);
        }
        return item as Form;
      }

      // Handle direct response
      if ('formValue' in responseData) {
        return this.parseFormValue(responseData as DynamicFormApiItem);
      }

      return responseData as Form;
    } catch (error) {
      logger.error('Failed to fetch form:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async createForm(organizationId: string, data: CreateFormDTO): Promise<Form> {
    try {
      const headers = await this.getAuthHeaders();

      // Generate formId from formName (lowercase, replace spaces with underscores)
      const formId = data.formName
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_');

      // Build the payload with fields as top-level properties
      const payload = {
        formId,
        formName: data.formName,
        fields: data.fields,
        triggerPrompt: data.triggerPrompt,
        description: data.description,
        requiresVerification: data.requiresVerification ?? false,
        saveLocal: true,
        successMessage: data.successMessage,
        errorMessage: data.errorMessage,
        sendEmailCopy: data.sendEmailCopy ?? false,
        emailIds: data.emailIds ?? [],
        isActive: true,
        serviceType: 'dynamic_forms',
      };

      const response = await httpClient.post<SingleDynamicFormApiResponse | SingleFormResponse>(
        `${this.basePath}/${organizationId}/dynamic-forms`,
        payload,
        { headers }
      );

      logger.info('Form created successfully');
      const responseData = response.data;

      // Build return form object
      const formData: Form = {
        formId,
        clientId: organizationId,
        formName: data.formName,
        triggerPrompt: data.triggerPrompt,
        description: data.description,
        requiresVerification: data.requiresVerification ?? false,
        fields: data.fields,
        saveLocal: true,
        successMessage: data.successMessage,
        errorMessage: data.errorMessage,
        sendEmailCopy: data.sendEmailCopy ?? false,
        emailIds: data.emailIds ?? [],
        isActive: true,
      };

      // Handle response with formValue
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const item = (responseData as { data: DynamicFormApiItem | Form }).data;
        if ('formValue' in item) {
          return this.parseFormValue(item as DynamicFormApiItem);
        }
        return item as Form;
      }

      if ('formValue' in responseData) {
        return this.parseFormValue(responseData as unknown as DynamicFormApiItem);
      }

      return formData; // Return what we sent if response doesn't include it
    } catch (error) {
      logger.error('Failed to create form:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  // dynamicFormUuid should be the uuid from the API response
  async updateForm(
    organizationId: string,
    dynamicFormUuid: string,
    data: UpdateFormDTO,
    formId?: string // Original formId for the payload
  ): Promise<Form> {
    try {
      const headers = await this.getAuthHeaders();

      // Build the payload with fields as top-level properties
      const payload = {
        formId: formId || dynamicFormUuid,
        formName: data.formName,
        fields: data.fields || [],
        triggerPrompt: data.triggerPrompt || '',
        description: data.description || '',
        requiresVerification: data.requiresVerification ?? false,
        saveLocal: true,
        successMessage: data.successMessage || '',
        errorMessage: data.errorMessage || '',
        sendEmailCopy: data.sendEmailCopy ?? false,
        emailIds: data.emailIds ?? [],
        isActive: true,
        serviceType: 'dynamic_forms',
      };

      const response = await httpClient.put<SingleDynamicFormApiResponse | SingleFormResponse>(
        `${this.basePath}/${organizationId}/dynamic-forms/${dynamicFormUuid}`,
        payload,
        { headers }
      );

      logger.info('Form updated successfully');
      const responseData = response.data;

      // Build return form object
      const formData: Form = {
        formId: formId || dynamicFormUuid,
        clientId: organizationId,
        formName: data.formName || '',
        triggerPrompt: data.triggerPrompt || '',
        description: data.description || '',
        requiresVerification: data.requiresVerification ?? false,
        fields: data.fields || [],
        saveLocal: true,
        successMessage: data.successMessage || '',
        errorMessage: data.errorMessage || '',
        sendEmailCopy: data.sendEmailCopy ?? false,
        emailIds: data.emailIds ?? [],
        isActive: true,
      };

      // Handle response with formValue
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const item = (responseData as { data: DynamicFormApiItem | Form }).data;
        if ('formValue' in item) {
          return this.parseFormValue(item as DynamicFormApiItem);
        }
        return item as Form;
      }

      if ('formValue' in responseData) {
        return this.parseFormValue(responseData as unknown as DynamicFormApiItem);
      }

      return formData; // Return what we sent if response doesn't include it
    } catch (error) {
      logger.error('Failed to update form:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  // dynamicFormUuid should be the uuid from the API response
  async deleteForm(organizationId: string, dynamicFormUuid: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(
        `${this.basePath}/${organizationId}/dynamic-forms/${dynamicFormUuid}`,
        {
          headers,
        }
      );

      logger.info('Form deleted successfully');
    } catch (error) {
      logger.error('Failed to delete form:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  // Get all form submissions/values for a specific form
  async getFormValues(organizationId: string, formId: string): Promise<FormSubmission[]> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<FormValuesListResponse | FormSubmission[]>(
        `${this.basePath}/${organizationId}/dynamic-form-values/${formId}`,
        { headers }
      );

      const responseData = response.data;

      // Handle array response directly
      if (Array.isArray(responseData)) {
        return responseData;
      }

      // Handle {data: [...], count: ...} response
      if (responseData && 'data' in responseData && Array.isArray(responseData.data)) {
        return responseData.data;
      }

      return [];
    } catch (error) {
      logger.error('Failed to fetch form values:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  // Delete all form submissions/values for a specific form
  async deleteFormValues(organizationId: string, formId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(`${this.basePath}/${organizationId}/dynamic-form-values/${formId}`, {
        headers,
      });

      logger.info('Form values deleted successfully');
    } catch (error) {
      logger.error('Failed to delete form values:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async optimizePrompt(organizationId: string, aiPrompt: string): Promise<string> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<{ optimizedPrompt: string }>(
        `${this.basePath}/${organizationId}/optimize-prompt`,
        { aiPrompt },
        { headers }
      );
      return response.data.optimizedPrompt;
    } catch (error) {
      logger.error('Failed to optimize prompt:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  // Delete a single form submission by UUID
  async deleteFormValue(organizationId: string, formId: string, uuid: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(
        `${this.basePath}/${organizationId}/dynamic-form-values/${formId}/${uuid}`,
        { headers }
      );

      logger.info('Form value deleted successfully');
    } catch (error) {
      logger.error('Failed to delete form value:', error);
      return ApiErrorHandler.handle(error);
    }
  }
}

export const formEndpoints = FormEndpoints.getInstance();
